import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Mail, Loader2, CheckCircle, XCircle, Lock } from "lucide-react";

export default function ConfirmEmailChange() {
  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError(
        "Token de verificación no encontrado. El enlace puede ser inválido.",
      );
    }
  }, [token]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setError("Token no válido");
      return;
    }

    if (!password) {
      setError("Por favor, introduce tu contraseña");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/users/confirm-email-change`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        setNewEmail(data.newEmail || null);

        // Redirect to login after 5 seconds
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      } else {
        setError(data.message || "Error al confirmar el cambio de email");
      }
    } catch (err) {
      setError("Error de conexión. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Email actualizado!
          </h1>
          <p className="text-gray-600 mb-4">
            Tu correo electrónico ha sido cambiado correctamente
            {newEmail && (
              <span>
                {" "}
                a <strong className="text-gray-900">{newEmail}</strong>
              </span>
            )}
            .
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Serás redirigido a la página de inicio de sesión en unos segundos...
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Ir a Iniciar Sesión
          </Link>
        </div>
      </div>
    );
  }

  // Error state (no token)
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Enlace inválido
          </h1>
          <p className="text-gray-600 mb-6">
            El enlace de verificación no es válido o ha expirado. Por favor,
            solicita un nuevo cambio de email desde tu perfil.
          </p>
          <Link
            to="/settings"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Ir a Configuración
          </Link>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 text-center">
            Confirmar cambio de email
          </h1>
          <p className="text-gray-600 text-center mt-2">
            Por seguridad, introduce tu contraseña para confirmar el cambio de
            correo electrónico.
          </p>
        </div>

        <form onSubmit={handleConfirm} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Lock className="w-4 h-4 inline mr-1" />
              Contraseña
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Introduce tu contraseña"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              Esta es la contraseña de tu cuenta actual
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !password}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Confirmando...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Confirmar cambio de email
              </>
            )}
          </button>

          <div className="text-center pt-2">
            <Link
              to="/settings"
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancelar y volver a Configuración
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
